import { Skeleton } from "@mui/material"
import { Subscribe } from "@react-rxjs/core"
import { t } from "i18next"
import { useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { InstrumentHeader } from "@/App/Equities/mobile/components/instrumentPage/InstrumentHeader"
import { SimpleViewLayout } from "@/App/Equities/mobile/layout"
import { RootPath } from "@/App/Equities/mobile/paths"
import {
  BalanceDetails,
  IndicativeCost,
  OrderErrorAlert,
  OrderTabs,
  SubmitOrderButton,
} from "@/App/Equities/shared/components"
import { DetailField } from "@/App/Equities/shared/components/order/DetailField"
import { Order, useResponse } from "@/App/Equities/shared/services/orders"
import { Direction, OrderStatus } from "@/generated/TradingGateway"

import { BottomBar } from "../../utils"
import { TRADE_TICKET_LOCATORS } from "./locators"

export type AcceptedOrderNavigationState = {
  response: Order
}
export const TradeTicket = () => {
  const { symbol = "", direction } = useParams()
  const { isLoading, response } = useResponse()
  const navigate = useNavigate()
  const isSell = direction === Direction.Sell

  useEffect(() => {
    if (!isLoading && response) {
      if (response.type === OrderStatus.Accepted) {
        const acceptedOrderState: AcceptedOrderNavigationState = { response }
        navigate(
          RootPath.tradeInstrumentTradeReceipt(symbol, direction as Direction),
          { state: acceptedOrderState },
        )
      }
    }
  }, [isLoading, response, direction, symbol, navigate])

  return (
    <SimpleViewLayout
      back={{ to: RootPath.tradeInstrument(symbol) }}
      header={
        <InstrumentHeader
          symbol={symbol}
          data-testid={TRADE_TICKET_LOCATORS.root}
        />
      }
      routePattern={RootPath.tradeInstrumentTrade.routePattern}
      data-testid=""
    >
      <OrderTabs showDirectionToggle={false} centerTabs />
      <Subscribe fallback={<Skeleton />}>
        <DetailField
          label={t("orderIndicativeCost")}
          testId="orderIndicativeCost"
        >
          <IndicativeCost symbol={symbol} />
        </DetailField>
      </Subscribe>
      <BalanceDetails />

      <BottomBar>
        <SubmitOrderButton
          symbol={symbol}
          direction={direction as Direction}
          fullWidth
          sx={({ palette, uisk: { radius } }) => {
            const colors = {
              color: palette[isSell ? "text-white" : "text-black"],
              backgroundColor:
                palette[isSell ? "bg-sell-primary" : "bg-buy-primary"],
            }
            return {
              height: 64,
              fontSize: 20,
              borderRadius: radius.md,
              ...colors,
              // Theme's brand variant sets its own hover colours
              "&:hover": colors,
              "&.Mui-disabled": {
                backgroundColor: "transparent",
                border: `1px solid ${palette["border-secondary"]}`,
                color: palette["text-disabled"],
              },
            }
          }}
        />
      </BottomBar>

      <OrderErrorAlert />
    </SimpleViewLayout>
  )
}
